import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Payment } from './components/Payment/Payment';
import { ScreenDrawer } from './components/ScreenDrawer/ScreenDrawer';
import { apiClient } from './services/apiClient';
import type {
  AddressDetails,
  ICheckoutContext,
  RedirectPaymentContext,
  SuccessScreenContext,
  ThreeDSSecurityState,
} from './types/context';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod, ProviderEnum, Templates } from './types/common';
import type { DrawerScreen } from './types/screens';
import type { InitialCardPayload, PriceBreakdown, SavedCard } from './types/checkout';
import { useCheckoutData } from './api/useCheckoutData';
import { calculateTotalPrice } from './utils/pricing';
import { CheckoutContext } from './context/CheckoutContext';
import { Modal } from './components/Common/Modal';
import { organizePaymentMethods } from './utils/paymentMethods';
import { useCheckout } from './hooks/useCheckout';
import { useGetBoot } from './hooks/useGetBoot';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { usePayment } from './hooks/usePayment';
import { stripeService } from './services/stripe';
import { useCardElementRef } from './hooks/useStripeElements';
import { paymentHandler } from './services/paymentHandler';
import { usePaymentToServer } from './hooks/usePaymentToServer';
import { SESSION_ID } from './constants/params';
import { isLandscape, isMobileDevice } from './utils/device';
import { useIsMobile } from './hooks/useIsMobile';
import { STRIPE_CONFIG } from './constants/stripe';
import { useApplicationState } from './hooks/useApplicationState';
import { XButton } from './components/XButton/XButton';
import { RedirectPayment } from './components/RedirectPayment/RedirectPayment';
import { SuccessScreen } from './components/SuccessScreen/SuccessScreen';
import { useOrderPolling } from './hooks/useOrderPolling';
import { PollingStatus } from './types/order';
import { ProcessingLoader } from './components/ProcessingLoader/ProcessingLoader';
import { useInitilizeAdyen } from './hooks/useInitilizeAdyen';
import { Card3DSForm } from './components/Card3DSForm/Card3DSForm';
import type { AppchargeErrorType, BasePaymentResponse, PayFromExpressCheckout } from './types/payment';
import { useCreateSession } from './api/session';
import { usePaymentResults } from './hooks/usePaymentResults';
import {
  notifyCheckoutOpened,
  notifyOrderCreated,
  notifyPaymentIntentSuccess,
  notifyCloseCheckout,
  notifyOrderSuccess,
  notifyOrderFailed,
  notifyPaymentIntentFailed,
} from './utils/notifier';
import { forterService } from './services/forterService';
import { getForterToken } from './utils/forter';
import { StripePaymentHandlerPage } from './pages/StripePaymentHandlerPage';
import { getSessionIdFromUrlParams, shouldRenderPaymentHandlerPage } from './utils/location';
import { useTemplate } from './hooks/useTemplate';
import { getRedirectLinkFromUrlParams } from './utils/params';
import { getStripePaymentKey } from './utils/stripe';
import { initializeTemplateFromBootConfig } from './utils/templateInitializer';
import { OnePaymentTemplate } from './components/Templates/OnePaymentTemplate';
import { initCoralogixRum, logError, logInfo, updateRumCustomerData } from './utils/logger';
import { mixpanelService } from './services/mixpanelService';
import { initI18n } from './i18n';
import { PaymentMethodTypeToEnum } from './constants/paymentMethodTypeMapping';
import { Maintenance } from './components/Maintenance/Maintenance';
import { CheckoutTemplate } from './types/boot';
import { getEventDataForSdkEvents } from './utils/eventDataBuilder';
import { useTranslate } from './hooks/useTranslate';
import { TopWrapper } from './components/TopWrapper/TopWrapper';
import { APPCHARGE_ERROR_TYPE_TO_KEY } from './constants/error';
import { getTabUrlPreparedForRedirect, handleOpenTab, isRequireToOpenTab } from './utils/redirectPayment';
import { ErrorLogs, InfoLogs } from './constants/logs';
import { getAllowDefaultCheckSavedCardBox } from './utils/useAllowDefaultCheckSavedCardBox';
import CheckoutSkeletonLoader from './components/CheckoutSkeletonLoader/CheckoutSkeletonLoader';
import { useErrorBoundary } from 'react-error-boundary';
import { trackCheckoutOpened } from './utils/checkoutMixpanelTracking';
import type { PromoCodeData } from './services/iframeMessenger';
import { checkIfServerFinalizeFailure } from '@/utils/error';

export const CheckoutContent = () => {
  const { showBoundary } = useErrorBoundary();
  const [searchParams] = useSearchParams();
  const sessionId = getSessionIdFromUrlParams();
  const bootData = useGetBoot();
  const checkout = useCheckout();
  const payment = usePayment();
  const templateHook = useTemplate();
  const { cardElementRef, setCardElementRef } = useCardElementRef();
  const [redirectPayment, setRedirectPayment] = useState<RedirectPaymentContext>();
  const openedTabRef = useRef<Window | null>(null);
  const [successScreen, setSuccessScreen] = useState<SuccessScreenContext>();
  const [threeDSSecurityState, setThreeDSSecurityState] = useState<ThreeDSSecurityState>({ show3DSForm: false });
  const [error, setError] = useState<string | null>(null);
  const paymentToServer = usePaymentToServer();
  const isInitialized = useRef(false);
  const { adyenCardDetailsRef } = useInitilizeAdyen();
  const [isApiInitilized, setIsApiInitilized] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const checkoutData = useCheckoutData({
    enableFetch: isApiInitilized && !bootData.isLoading,
  });
  const lastPaymentMethodUsedRef = useRef<PaymentMethodEnum>();
  const { mutateAsync: createSession } = useCreateSession();
  const [showMaintenance, setShowMaintenance] = useState(false);
  const { t } = useTranslate();
  const { isDefaultSaveCardAllowedForLocation } = getAllowDefaultCheckSavedCardBox({
    customer: checkout.customer,
    addressDetails: checkout.addressDetails,
    featureFlags: bootData.boot?.featureFlags,
  });

  const { triggerOrderPolling, statusOrderPolling, data: orderData } = useOrderPolling();

  const sendPaymentToServer = useCallback(
    async <TResponse = unknown,>(params: unknown): Promise<TResponse> => {
      const forterToken = getForterToken();

      const enrichedParams = {
        ...(params as Record<string, unknown>),
        params: {
          ...((params as Record<string, unknown>).params as Record<string, unknown>),
          fraudDetection: {
            forterTokenCookie: forterToken,
          },
        },
      };

      try {
        return (await paymentToServer.mutateAsync(enrichedParams)) as TResponse;
      } catch (err) {
        throw err;
      }
    },
    [paymentToServer]
  );

  const closeDrawer = useCallback(() => {
    if (isMobileDevice()) {
      checkout.setDrawerScreen(undefined);
    } else {
      checkout.setViewer(undefined);
    }
  }, [checkout]);

  const clearState = useCallback(() => {
    setError(null);
    setSuccessScreen(undefined);
    payment.setIsExpressPaymentInProcess(false);
    setThreeDSSecurityState({ show3DSForm: false });
    payment.setIsInPaymentProcess(false);
  }, []);

  const saveNewCard = useCallback(
    (card: SavedCard) => {
      checkout.setSavedCards([...checkout.savedCards, { ...card, isNew: true }]);
      checkout.setSelectedCard({ ...card, isNew: true });
    },
    [checkout]
  );

  const handlePaymentError = useCallback(
    (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum) => {
      logError(ErrorLogs.checkoutPaymentError, {
        error: appChargeErrorType,
        provider,
      });
      const errorMessage = appChargeErrorType
        ? t(`appchargeErrors.${APPCHARGE_ERROR_TYPE_TO_KEY[appChargeErrorType]}`)
        : t('somethingWentWrong');
      setError(errorMessage);
      notifyPaymentIntentFailed(checkoutData.data!, lastPaymentMethodUsedRef.current as PaymentMethodEnum);
      if (provider && isRequireToOpenTab(provider)) {
        closeRedirectTab();
      }
    },
    [t, showBoundary, checkoutData.data!]
  );

  const handlePaymentSuccess = useCallback(
    (result: BasePaymentResponse, provider: ProviderEnum) => {
      logInfo(InfoLogs.checkoutPaymentSuccess, {
        result,
        provider,
      });
      triggerOrderPolling(result.orderId);
      setSuccessScreen({
        isOpen: true,
        paymentDetails: { params: result },
        provider,
      });
      notifyPaymentIntentSuccess({
        ...getEventDataForSdkEvents(checkoutData.data!),
        paymentMethodName: lastPaymentMethodUsedRef.current,
        promoCode: checkout.promoCode ?? null,
      });
    },
    [triggerOrderPolling, checkoutData.data, checkout.promoCode]
  );

  const { handlePaymentResults } = usePaymentResults({
    paymentMethods: checkout.paymentMethods,
    onSuccess: handlePaymentSuccess,
    onError: handlePaymentError,
    onRedirect: (params: RedirectPaymentContext) => setRedirectPayment((prev) => ({ ...prev, ...params })),
    on3DSRequired: setThreeDSSecurityState,
    actions: {
      stopPaymentProcess: () => {
        payment.setIsInPaymentProcess(false);
        payment.setIsExpressPaymentInProcess(false);
      },
    },
  });

  const initilizeCheckout = () => {
    const checkoutSessionId = searchParams.get(SESSION_ID) || sessionId;
    if (!checkoutSessionId || isApiInitilized) return;

    apiClient.initilize({ token: checkoutSessionId });
    forterService.initialize();
    mixpanelService.initialize();
    trackCheckoutOpened(checkoutSessionId, checkoutContext);

    initI18n(checkoutSessionId).catch((error) => {
      console.error('Failed to initialize i18n:', error);
    });

    setIsApiInitilized(true);
  };

  const openRedirectTab = () => {
    const openedTab = handleOpenTab(getTabUrlPreparedForRedirect());
    openedTabRef.current = openedTab;
    setRedirectPayment({ openedTab });
  };

  const closeRedirectTab = () => {
    if (openedTabRef.current) {
      openedTabRef.current.close();
      openedTabRef.current = null;
      setRedirectPayment(undefined);
    }
  };
  useEffect(() => {
    clearState();
  }, [checkout.paymentMethodSelected, checkout.otherMethodSelected, clearState]);

  useEffect(() => {
    if (statusOrderPolling === PollingStatus.SUCCESS) {
      payment.setIsInPaymentProcess(false);
      payment.setIsExpressPaymentInProcess(false);
      if (orderData) {
        checkout.setOrderData(orderData);
      }
      notifyOrderSuccess({
        ...getEventDataForSdkEvents(checkoutData.data!),
        paymentMethodName: lastPaymentMethodUsedRef.current,
        promoCode: checkoutContext?.promoCode ?? null,
      });
    }
    if (statusOrderPolling === PollingStatus.FAILED) {
      showBoundary('unableToCompleteOrder');
      notifyOrderFailed({
        ...getEventDataForSdkEvents(checkoutData.data!),
        paymentMethodName: lastPaymentMethodUsedRef.current,
        promoCode: checkoutContext?.promoCode ?? null,
      });
    }
  }, [statusOrderPolling]);

  useEffect(() => {
    initilizeCheckout();
  }, [searchParams, sessionId, isApiInitilized]);

  const initilizeCheckoutData = useCallback(() => {
    try {
      if (!checkoutData.data || !bootData.boot || isInitialized.current || !isApiInitilized) return;
      notifyCheckoutOpened(getEventDataForSdkEvents(checkoutData.data));

      if (checkoutData?.data?.savedCards?.length) {
        checkout.setSelectedCard(checkoutData.data.savedCards[0]);
      }

      const checkoutConfig = checkoutData.data.checkoutConfigurations;
      let paymentMethodsToUse = checkoutData.data.paymentMethods;
      let paymentMethodsAvailableToUse = [...checkoutData.data.paymentMethods];

      if (checkoutConfig?.template === CheckoutTemplate.SINGLE_PAYMENT_METHOD) {
        if (!checkoutConfig?.availablePaymentMethods || checkoutConfig.availablePaymentMethods.length === 0) {
          setError(t('paymentMethodNotAvailable'));
          return;
        }

        const availableEnums = checkoutConfig.availablePaymentMethods
          .map((type) => PaymentMethodTypeToEnum[type])
          .filter(Boolean) as PaymentMethodEnum[];

        if (availableEnums.length === 0) {
          setError(t('paymentMethodNotAvailable'));
          return;
        }

        paymentMethodsToUse = paymentMethodsToUse.filter((pm) =>
          availableEnums.includes(pm.paymentMethodName as PaymentMethodEnum)
        );
        paymentMethodsAvailableToUse = paymentMethodsAvailableToUse.filter((pm) =>
          availableEnums.includes(pm.paymentMethodName as PaymentMethodEnum)
        );

        const requiredPaymentMethod = availableEnums[0];
        const hasRequiredMethod = paymentMethodsAvailableToUse.some(
          (pm) => pm.paymentMethodName === requiredPaymentMethod
        );

        if (!hasRequiredMethod || paymentMethodsAvailableToUse.length === 0) {
          logError(ErrorLogs.checkoutPaymentMethodNotAvailable, {
            error: t('paymentMethodNotAvailable'),
          });
          setError(t('paymentMethodNotAvailable'));
          return;
        }
      } else if (checkoutConfig?.availablePaymentMethods && checkoutConfig.availablePaymentMethods.length > 0) {
        const availableEnums = checkoutConfig.availablePaymentMethods
          .map((type) => PaymentMethodTypeToEnum[type])
          .filter(Boolean) as PaymentMethodEnum[];

        paymentMethodsToUse = paymentMethodsToUse.filter((pm) =>
          availableEnums.includes(pm.paymentMethodName as PaymentMethodEnum)
        );
        paymentMethodsAvailableToUse = paymentMethodsAvailableToUse.filter((pm) =>
          availableEnums.includes(pm.paymentMethodName as PaymentMethodEnum)
        );
      }

      const organizedPaymentMethods = organizePaymentMethods(paymentMethodsAvailableToUse, checkoutConfig);

      checkout.setPaymentMethodsAvailable(organizedPaymentMethods);
      checkout.setPurchasedItems(checkoutData.data.items);
      checkout.setCustomer(checkoutData.data.customer);
      checkout.setPaymentMethods(paymentMethodsToUse);
      checkout.setPaymentKeys(checkoutData.data.pks);
      checkout.setPublisher(bootData.boot?.publisherData);
      checkout.setPricing(checkoutData.data.pricing);
      checkout.setSavedCards(checkoutData.data.savedCards);
      checkout.setCheckoutConfigurations(checkoutConfig);
      checkout.setRedirectLink(checkoutData.data.redirectLink ?? getRedirectLinkFromUrlParams());
      checkout.setOffer(checkoutData.data.offer);
      checkout.setOrderId(checkoutData.data.orderId);
      checkout.setAddressDetails({
        address: checkoutData.data.customer?.address?.address ?? '',
        zipCode: checkoutData.data.customer?.address?.zipCode ?? '',
      });

      // Check template from checkoutConfigurations first, then fall back to boot configuration
      const configTemplate = checkoutConfig?.template
        ? { template: checkoutConfig.template, availablePaymentMethods: checkoutConfig.availablePaymentMethods }
        : bootData.boot?.checkoutConfiguration;

      const { template: initializedTemplate } = initializeTemplateFromBootConfig(configTemplate, templateHook.template);

      templateHook.setTemplate(initializedTemplate);

      const firstNonExpressMethod = organizedPaymentMethods.find((paymentMethod) => !paymentMethod.isExpress);
      const defaultPaymentMethod = firstNonExpressMethod ?? organizedPaymentMethods[0];

      if (defaultPaymentMethod) {
        checkout.setPaymentMethodSelected(defaultPaymentMethod.paymentMethodName as PaymentMethodEnum);
      }

      // Update Coralogix RUM with customer data when available
      const checkoutSessionId = searchParams.get(SESSION_ID) || sessionId;
      if (checkoutSessionId && checkoutData.data.customer?.email) {
        updateRumCustomerData(checkoutSessionId, checkoutData.data.customer.email);
      }
      initCoralogixRum(checkoutData.data.customer?.email, bootData.boot);
      notifyOrderCreated(getEventDataForSdkEvents(checkoutData.data!));
      checkout.setDataIsReady(true);
      isInitialized.current = true;
    } catch (error) {
      showBoundary('unableToOpenCheckout');
    }
  }, [checkoutData.data, bootData.boot, checkout, isApiInitilized, templateHook, searchParams, sessionId]);

  const closeCheckout = useCallback((context: ICheckoutContext, reason?: string) => {
    notifyCloseCheckout(getEventDataForSdkEvents(context!, { error: { reason } }));
  }, []);

  const checkoutContext: ICheckoutContext | undefined = useMemo(() => {
    if (!checkout.dataIsReady || !isApiInitilized) return;

    const context: ICheckoutContext = {
      promoCode: checkout.promoCode,
      paymentMethodsAvailable: checkout.paymentMethodsAvailable,
      paymentMethodSelected: checkout.paymentMethodSelected,
      boot: bootData.boot!,
      redirectLink: checkout.redirectLink ?? '',
      showMaintenance: showMaintenance,
      orderId: checkout.orderId,
      sessionId: sessionId,
      actions: {
        setPromoCode: (promoCode: PromoCodeData | null) => checkout.setPromoCode(promoCode),
        showMaintenance: () => setShowMaintenance(true),
        setRedirectLink: (redirectLink: string) => checkout.setRedirectLink(redirectLink),
        startOrderPolling: (orderId: string) => triggerOrderPolling(orderId),
        removeCard: (card: SavedCard) => checkout.handleRemoveCard(card),
        closeCheckout,
        createSession,
        setIsExpressPaymentInProcess: () => payment.setIsExpressPaymentInProcess(true),
        setIsFormValid: payment.setIsFormValid,
        setRedirectPayment: (redirectPayment: RedirectPaymentContext) => setRedirectPayment(redirectPayment),
        updateAddressDetails: (addressDetails: AddressDetails) => checkout.setAddressDetails(addressDetails),
        setCustomerEmail: (email: string) => checkout.setCustomer({ ...checkout.customer!, email }),
        setPaymentMethodSelected: (paymentMethod: PaymentMethodEnum) =>
          checkout.setPaymentMethodSelected(paymentMethod as PaymentMethodEnum),
        updateOtherMethodSelected: (method: ExcludeOtherPaymentMethod) => checkout.setOtherMethodSelected(method),
        setIsInPaymentProcess: payment.setIsInPaymentProcess,
        setThreeDSSecurityState: (threeDSSecurityState: ThreeDSSecurityState) =>
          setThreeDSSecurityState(threeDSSecurityState),
        runPayment: async <TResponse = unknown,>(
          params?: unknown,
          payFromExpressCheckout?: PayFromExpressCheckout,
          provider?: ProviderEnum
        ): Promise<TResponse | undefined> => {
          try {
            clearState();
            logInfo(InfoLogs.checkoutPaymentStarted, {
              provider,
              payFromExpressCheckout,
            });
            const selectedPaymentMethod = payFromExpressCheckout
              ? payFromExpressCheckout
              : checkout.paymentMethodSelected === PaymentMethodEnum.OTHER
                ? checkout.otherMethodSelected
                : checkout.paymentMethodSelected;
            lastPaymentMethodUsedRef.current = selectedPaymentMethod as PaymentMethodEnum;
            if (payFromExpressCheckout) {
              payment.setIsExpressPaymentInProcess(true);
            } else {
              payment.setIsInPaymentProcess(true);
            }
            if (!payFromExpressCheckout && provider && isRequireToOpenTab(provider)) {
              openRedirectTab();
            }
            const results = (await paymentHandler.processPayment(
              selectedPaymentMethod as PaymentMethodEnum,
              context,
              sendPaymentToServer,
              params
            )) as TResponse;
            return handlePaymentResults(
              results,
              selectedPaymentMethod as PaymentMethodEnum,
              checkout.paymentKeys
            ) as TResponse;
          } catch (error: unknown) {
            logError(ErrorLogs.checkoutPaymentError, {
              error,
            });
            const isServerFinalizeFailure = checkIfServerFinalizeFailure(error as Error);
            if (isServerFinalizeFailure) {
              showBoundary('unableToProcessPayment');
            } else {
              setError(t('somethingWentWrong'));
            }
            notifyPaymentIntentFailed(checkoutData?.data!, lastPaymentMethodUsedRef.current as PaymentMethodEnum);
            payment.setIsInPaymentProcess(false);
            payment.setIsExpressPaymentInProcess(false);
            closeRedirectTab();
          }
        },
        sendPaymentToServer,
        updatePriceBreakdown: (priceBreakdown: PriceBreakdown) =>
          checkout.setPricing({ ...checkout.pricing!, priceBreakdown }),
        setSuccessScreen: (successScreen: SuccessScreenContext) => setSuccessScreen(successScreen),
        setError: (error: string | null) => setError(error),
        setOrderId: (orderId: string | undefined) => checkout.setOrderId(orderId),
        setSelectedCard: (card: SavedCard) => checkout.setSelectedCard(card),
      },
      redirectPayment: redirectPayment ?? {},
      otherMethodSelected: checkout.otherMethodSelected,
      publisherId: bootData.boot?.publisherData.id || '',
      isLoading: false,
      template: templateHook.template,
      totalPrice: calculateTotalPrice(checkoutData.data!.pricing),
      purchasedItems: checkout.purchasedItems,
      customer: checkout.customer!,
      paymentMethodsSettings: checkout.paymentMethods,
      publisher: checkout.publisher!,
      pricing: checkout.pricing!,
      offer: checkout.offer,
      featureFlags: bootData.boot!.featureFlags,
      viewer: {
        screen: checkout.viewer,
        setViewer: (screen?: DrawerScreen) => checkout.setViewer(screen),
        closeViewer: () => checkout.setViewer(undefined),
      },
      addressDetails: checkout.addressDetails,
      successScreen: successScreen ?? { isOpen: false, orderData: checkout.orderData },
      payment: {
        isInPaymentProcess: payment.isInPaymentProcess,
        isExpressPaymentInProcess: payment.isExpressPaymentInProcess,
        isFormValid: payment.isFormValid,
      },
      card: {
        savedCards: checkout.savedCards,
        selectedCard: checkout.selectedCard,
        selectCard: (card) => checkout.setSelectedCard(card),
        saveNewCard: (card) => checkout.setSelectedCard({ ...card, isNew: true }),
        cardElementRef: cardElementRef,
        setCardElementRef: setCardElementRef,
        adyenCardDetailsRef: adyenCardDetailsRef,
        initialCardPayload:
          checkout?.initialCardPayload ??
          ({
            isSaveCard: isDefaultSaveCardAllowedForLocation ?? false,
          } as InitialCardPayload),
        setInitialCardPayload: (initialCardPayload: InitialCardPayload) =>
          checkout.setInitialCardPayload(initialCardPayload),
      },
      paymentKeys: checkout.paymentKeys,
      drawer: {
        screen: checkout.drawerScreen,
        setDrawerScreen: (screen: DrawerScreen) => checkout.setDrawerScreen(screen),
        closeDrawer,
      },
      stripe: stripeInstance,
      checkoutConfiguration: checkout.checkoutConfiguration!,
      error,
      threeDSSecurityState: threeDSSecurityState,
    };

    return context;
  }, [
    checkout,
    checkoutData,
    bootData,
    closeDrawer,
    saveNewCard,
    payment,
    stripeInstance,
    cardElementRef,
    setCardElementRef,
    isApiInitilized,
    sendPaymentToServer,
    redirectPayment,
    successScreen,
    handlePaymentResults,
    error,
    threeDSSecurityState,
    adyenCardDetailsRef,
    clearState,
    closeCheckout,
    createSession,
    triggerOrderPolling,
    templateHook.template,
    showMaintenance,
  ]);

  useEffect(initilizeCheckoutData, [initilizeCheckoutData]);

  useEffect(() => {
    if (stripeInstance) return;

    const initializeStripe = async () => {
      const clientSecret = getStripePaymentKey(checkout.paymentKeys, checkout.paymentMethods);

      if (!clientSecret) return;

      const stripe = await loadStripe(clientSecret, STRIPE_CONFIG);

      if (stripe) {
        stripeService.initialize(stripe);
        setStripeInstance(stripe);
      }
    };

    initializeStripe();
  }, [checkout.paymentKeys, checkout.paymentMethods, stripeInstance]);

  if (!checkoutContext || !checkout.dataIsReady) {
    return <CheckoutSkeletonLoader />;
  }

  return (
    <CheckoutContext.Provider value={checkoutContext}>
      <CheckoutWrapper statusOrderPolling={statusOrderPolling} />
    </CheckoutContext.Provider>
  );
};

const CheckoutWrapper = ({ statusOrderPolling }: { statusOrderPolling: PollingStatus }) => {
  const context = useContext(CheckoutContext);
  const {
    redirectPayment: { redirectUrl, showRedirect },
    successScreen,
    template,
    actions: { closeCheckout },
    showMaintenance,
  } = context;
  useApplicationState();
  const isMobile = useIsMobile();

  if (showMaintenance) {
    return <Maintenance />;
  }

  if (shouldRenderPaymentHandlerPage()) {
    return <StripePaymentHandlerPage />;
  }

  if (template.type === Templates.OnePayment) {
    return <OnePaymentTemplate statusOrderPolling={statusOrderPolling} />;
  }

  return (
    <Modal>
      <div
        data-testid="checkout-container"
        className="flex flex-col sm:justify-center md:flex-row md:justify-start md:h-full overflow-y-auto md:overflow-y-hidden bg-white landscape:flex-row landscape:h-[100dvh] landscape:overflow-y-hidden landscape:overflow-x-hidden"
      >
        <TopWrapper />
        <div className="relative flex flex-col md:w-3/5 md:h-full md:overflow-y-auto landscape:w-3/5 landscape:h-[100dvh] landscape:overflow-y-auto">
          {(!isMobile || isLandscape()) && <XButton onClick={() => closeCheckout(context)} />}
          <Payment />
        </div>
        <Card3DSForm />
        <ProcessingLoader />
        {isMobile && <ScreenDrawer />}
        {(redirectUrl || showRedirect) && <RedirectPayment />}
        {successScreen?.isOpen && statusOrderPolling === PollingStatus.SUCCESS && <SuccessScreen />}
      </div>
    </Modal>
  );
};
